import { useCallback, useContext } from 'react';
import _get from 'lodash/get';
import { AjaxContext, AppContext, EMPTY_ARRAY, formatListData } from 'commons-ui';

import { UserDetailsContext } from 'context/userDetails';
import { ACTION, PAGE_NAMES, USER_PROFILE_RESOURCES } from 'constant';
import { KycBankNameMismatchRecord } from './constants';

/**
 * The `unauthorized` segment makes admin.service skip its per-user permission check and masking for
 * these calls, so the tab works for every logged-in user without permission rows. Drop that segment
 * to move back to the authorized routes, which still exist.
 */
const endpoint = (action: string): string =>
	`/dashboard/unauthorized/${PAGE_NAMES.userProfile}-${USER_PROFILE_RESOURCES.kycBankNameMismatch}-${action}`;

const useService = () => {
	const { app } = useContext(AppContext);
	const { postData } = useContext(AjaxContext);
	const {
		selectedUser: { uid }
	} = useContext(UserDetailsContext);

	/**
	 * The selected user's bank-name-mismatch rows from the configured window, newest attempt first.
	 *
	 * Only the newest is kept: approving any row approves the user and clears the rest, so showing
	 * older attempts invites a click that resolves to the same thing on a staler record.
	 */
	const getBankNameMismatchRejections = useCallback(async () => {
		const { appName } = app;
		const result = await postData({
			endpoint: endpoint(ACTION.getBankAccountNameMismatchRejectionsByUid),
			queryParams: { appName },
			body: { uid }
		});
		const records: KycBankNameMismatchRecord[] = _get(result, ['data', 'data', 'records'], EMPTY_ARRAY);
		// Sliced here rather than trusting the table's render order, which a later sort config could change.
		const latest: KycBankNameMismatchRecord[] = records.slice(0, 1);
		const lookbackDays: number = _get(result, ['data', 'data', 'lookbackDays'], 0);
		return { rowCount: latest.length, data: formatListData(latest), lookbackDays };
	}, [uid]);

	/** Approves a rejected KYC. */
	const overrideBankNameMismatchKyc = useCallback(async (kycId: number) => {
		const { appName } = app;
		const result = await postData({
			endpoint: endpoint(ACTION.overrideBankNameMismatchKyc),
			queryParams: { appName },
			body: { _id: kycId }
		});
		const success: boolean = _get(result, ['data', 'success'], false);
		const error: string = _get(result, ['data', 'error'], '');
		return { success, error };
	}, []);

	/** No per-user permission applies on the unauthorized routes, so every user may approve. */
	const canApprove = true;

	return { getBankNameMismatchRejections, overrideBankNameMismatchKyc, canApprove };
};

export default useService;
